import React, { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useBooking } from '../../services/bookingService';
import { paymentApi } from '../../services/paymentService';
import { useToastContext } from '../../contexts/ToastContext';
import ModalShell from '../shared/ModalShell';
import BalancePaymentModal from './BalancePaymentModal';

/**
 * Loads a booking + payments, then opens BalancePaymentModal.
 * Use from invoices list, bookings list, or after invoice accept.
 */
const ClientPayBookingModal = ({ bookingId, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const { error: toastError, success: toastSuccess } = useToastContext();

  const {
    data: bookingResponse,
    isLoading: bookingLoading,
    isError: bookingError,
  } = useBooking(bookingId, {
    enabled: Boolean(isOpen && bookingId),
  });

  const booking = bookingResponse?.data;

  const { data: payments = [], isLoading: paymentsLoading } = useQuery({
    queryKey: ['payments', bookingId],
    queryFn: () => paymentApi.getPaymentByBooking(bookingId),
    enabled: Boolean(isOpen && bookingId && booking),
    select: (response) => response?.data || [],
  });

  const balanceAmount = useMemo(() => {
    if (!booking) return 0;
    const subtotal = parseFloat(booking.subtotal_amount) || 0;
    const platformFee = parseFloat(booking.platform_fee) || 0;
    const amountPaid = parseFloat(booking.amount_paid) || 0;
    return Math.max(0, subtotal + platformFee - amountPaid);
  }, [booking]);

  const isLoading = Boolean(isOpen && bookingId && (bookingLoading || (booking && paymentsLoading)));

  useEffect(() => {
    if (!isOpen || !bookingId || bookingLoading) return;
    if (bookingError || !booking) {
      toastError(null, 'Could not load booking for payment.');
      onClose?.();
    }
  }, [isOpen, bookingId, bookingLoading, bookingError, booking, toastError, onClose]);

  useEffect(() => {
    if (!isOpen || !booking || isLoading) return;
    if (balanceAmount <= 0) {
      toastSuccess(null, 'This booking is already fully paid.');
      onClose?.();
    }
  }, [isOpen, booking, isLoading, balanceAmount, toastSuccess, onClose]);

  const handlePaymentSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['booking', bookingId] });
    queryClient.invalidateQueries({ queryKey: ['payments', bookingId] });
    queryClient.invalidateQueries({ queryKey: ['bookings'] });
    queryClient.invalidateQueries({ queryKey: ['client-invoices'] });
    queryClient.invalidateQueries({ queryKey: ['client-invoices-pending'] });
    toastSuccess(null, 'Payment successful.');
    onClose?.();
  };

  if (isOpen && bookingId && isLoading) {
    return (
      <ModalShell
        isOpen
        onClose={onClose}
        labelledBy="pay-booking-loading-title"
        className="items-center justify-center p-4"
        panelClassName="flex w-full max-w-sm flex-col items-center gap-3 rounded-xl border border-border bg-surface p-8 shadow-xl"
      >
        <Loader2 className="h-7 w-7 animate-spin text-ink" />
        <p id="pay-booking-loading-title" className="text-sm text-ink-secondary">
          Loading payment options…
        </p>
      </ModalShell>
    );
  }

  return (
    <BalancePaymentModal
      isOpen={Boolean(isOpen && booking && balanceAmount > 0)}
      onClose={onClose}
      booking={booking}
      balanceAmount={balanceAmount}
      payments={payments}
      onPaymentSuccess={handlePaymentSuccess}
      onPaymentError={() => {}}
    />
  );
};

export default ClientPayBookingModal;
